import React, { useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  Typography
} from "@mui/material";
import {
  BCPCopy,
  BCPDownToLine,
  BCPShareNodes,
  BCPXmark
} from "../icons/bcp/BCPIcons";
import {
  copyImageToClipboard,
  downloadImage,
  shareImageFile
} from "../utilities/useExportImage";
import { postNotification } from "../utilities/Notifications";

export default function ShareImageModal(props) {
  const {
    parentOpen,
    setParentOpen,
    imageUrl,
    imageBlob,
    filename = "export.png",
    title,
    loading
  } = props;
  const [sharing, setSharing] = useState(false);
  const [copying, setCopying] = useState(false);

  const canNativeShare =
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    Boolean(imageBlob);

  const canCopyImage =
    typeof navigator !== "undefined" &&
    Boolean(navigator.clipboard) &&
    typeof ClipboardItem !== "undefined" &&
    Boolean(imageBlob);

  const handleClose = () => {
    if (setParentOpen) setParentOpen(false);
  };

  const handleDownload = () => {
    if (!imageUrl) return;
    downloadImage(imageUrl, filename);
  };

  const handleCopy = async () => {
    if (!imageBlob) return;
    setCopying(true);
    try {
      const copied = await copyImageToClipboard(imageBlob);
      if (copied) {
        postNotification("success", "Image copied!");
      } else {
        postNotification(
          "info",
          "Copy image is not supported on this device. Try Download instead."
        );
      }
    } catch (err) {
      postNotification("error", "Unable to copy image");
    } finally {
      setCopying(false);
    }
  };

  const handleShare = async () => {
    if (!imageBlob) return;
    setSharing(true);
    try {
      const shared = await shareImageFile(imageBlob, filename, title);
      if (!shared) {
        downloadImage(imageUrl, filename);
        postNotification(
          "info",
          "Sharing is not supported on this device. Image downloaded instead."
        );
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        postNotification("error", "Unable to share image");
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <Modal
      disableAutoFocus
      open={Boolean(parentOpen)}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
      sx={{ zIndex: 9999 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: ["95vw", "85vw", 600],
          maxHeight: "95vh",
          bgcolor: "modal.background",
          color: "modal.text",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
          textAlign: "center",
          overflow: "auto"
        }}
      >
        <IconButton
          aria-label="cancel"
          color="error"
          sx={{ position: "absolute", top: "4px", right: "4px" }}
          onClick={handleClose}
        >
          <BCPXmark />
        </IconButton>
        <Grid
          container
          spacing={2}
          sx={{
            pb: 1,
            mb: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
          }}
        >
          <Grid item xs>
            <Typography variant="h4" component="h2">
              {title || "Share Image"}
            </Typography>
          </Grid>
        </Grid>

        <Box
          sx={{
            mb: 3,
            minHeight: 120,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          {loading || !imageUrl ? (
            <CircularProgress size={36} />
          ) : (
            <Box
              component="img"
              src={imageUrl}
              alt={title || "Export preview"}
              sx={{
                maxWidth: "100%",
                maxHeight: "55vh",
                objectFit: "contain",
                borderRadius: 1,
                border: "1px solid rgba(0, 0, 0, 0.12)"
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1.5,
            flexWrap: "wrap"
          }}
        >
          {canCopyImage && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<BCPCopy />}
              onClick={handleCopy}
              disabled={loading || !imageBlob || copying}
            >
              Copy
            </Button>
          )}
          {canNativeShare && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<BCPShareNodes />}
              onClick={handleShare}
              disabled={loading || !imageBlob || sharing}
            >
              Share
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<BCPDownToLine />}
            onClick={handleDownload}
            disabled={loading || !imageUrl}
          >
            Download
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
